import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  Avatar,
  Drawer,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerHeaderSx,
  harmonateGridActionIconSx,
  harmonateSwitchSx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionDetailsSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import PropertyImportDialog from './PropertyImportDialog';

const yesNoChipCell = (params) => {
  const v = params.value;
  if (v !== 'Yes' && v !== 'No') return '';
  const isYes = v === 'Yes';
  return (
    <Chip
      label={v}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 500,
        bgcolor: isYes ? ht.secondaryTab : ht.listBg,
        color: isYes ? ht.text : ht.text,
        borderRadius: ht.buttonRadius,
        '& .MuiChip-label': { px: 1.5 },
      }}
    />
  );
};

const defaultForm = {
  pms_property_id: '',
  customer_property_id: '',
  merchant_id: '',
  property_name: '',
  legal_name: '',
  property_code: '',
  property_type: 'HOTEL',
  property_group_id: '',
  room_count: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state_province: '',
  workforce_state_id: '',
  overtime_policy_name: '',
  job_code_level_2: '',
  postal_code: '',
  country: '',
  latitude: '',
  longitude: '',
  timezone: '',
  phone: '',
  email: '',
  currency: 'USD',
  default_shift_hours: 8,
  max_hours_per_employee: 40,
  allow_overtime: 'YES',
  overtime_threshold_hours: 40,
  labor_cost_budget: '',
  is_corporate_entity: 'NO',
  status: 'ACTIVE',
  created_by: '',
  updated_by: '',
  logo_url: '',
  logo_s3_key: '',
};

const OT_LINE_STATUSES = ['ACTIVE', 'INACTIVE'];

const DEFAULT_OT_RULE_OPTIONS = [
  { code: 'DAILY_OT', label: 'Daily Overtime', defaultThreshold: 8, defaultMultiplier: 1.5 },
  { code: 'WEEKLY_OT', label: 'Weekly Overtime', defaultThreshold: 40, defaultMultiplier: 1.5 },
  { code: 'DOUBLE_TIME', label: 'Double Time', defaultThreshold: 12, defaultMultiplier: 2 },
  { code: 'SEVENTH_DAY', label: 'Seventh Day', defaultThreshold: 8, defaultMultiplier: 2 },
  { code: 'CONSECUTIVE_HOURS', label: 'Consecutive Hours', defaultThreshold: 12, defaultMultiplier: 1.5 },
];

const defaultOtForm = {
  overtime_rule_id: '',
  rule_name: '',
  condition_type: '',
  threshold_hours: '',
  multiplier: '1.5',
  status: 'ACTIVE',
};

const mapOtRow = (item) => ({
  id: item.overtime_rule_id || item.overtimeRuleId,
  overtime_rule_id: item.overtime_rule_id || item.overtimeRuleId,
  rule_name: item.rule_name || item.ruleName || '',
  condition_type: item.condition_type || item.conditionType || '',
  threshold_hours: item.threshold_hours ?? item.thresholdHours ?? 0,
  multiplier: item.multiplier ?? 1.5,
  status: item.status || 'ACTIVE',
});

const AUTO_ASSIGN_SELECTION_MODES = [
  { code: 'LEAST_WEEKLY_HOURS', label: 'Least weekly hours (fair load)' },
  { code: 'LEAST_WEEKLY_SHIFTS', label: 'Least weekly shifts' },
  { code: 'ROUND_ROBIN', label: 'Round robin' },
  { code: 'EMPLOYEE_ID_ASC', label: 'Stable employee order (legacy)' },
];

const defaultAutoAssignForm = {
  auto_assign_policy_id: '',
  enabled: false,
  selection_mode: 'EMPLOYEE_ID_ASC',
  max_shifts_per_day: '',
  min_rest_hours_between_shifts: '',
  max_consecutive_work_days: '',
  prefer_preferred_availability: true,
  status: 'ACTIVE',
};

const mapAutoAssignPolicy = (item) => ({
  auto_assign_policy_id: item?.auto_assign_policy_id || item?.autoAssignPolicyId || '',
  enabled: item?.enabled === true,
  selection_mode: item?.selection_mode || item?.selectionMode || 'EMPLOYEE_ID_ASC',
  max_shifts_per_day: item?.max_shifts_per_day ?? item?.maxShiftsPerDay ?? '',
  min_rest_hours_between_shifts:
    item?.min_rest_hours_between_shifts ?? item?.minRestHoursBetweenShifts ?? '',
  max_consecutive_work_days:
    item?.max_consecutive_work_days ?? item?.maxConsecutiveWorkDays ?? '',
  prefer_preferred_availability:
    item?.prefer_preferred_availability ?? item?.preferPreferredAvailability ?? true,
  status: item?.status || 'ACTIVE',
});

const Property = () => {
  const { canAdd, canEdit, canDelete, canUpload } = useFunctionPermissions(FC.PROPERTY);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';
  const tenantIntegration = localStorage.getItem('tenant_integration') || '';
  const username = localStorage.getItem('username') || '';

  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState('add');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [propertyImportOpen, setPropertyImportOpen] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [propertyGroupsLoading, setPropertyGroupsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [configDrawerRow, setConfigDrawerRow] = useState(null);
  const [drawerAccordionExpanded, setDrawerAccordionExpanded] = useState('overtime');
  const [otLoading, setOtLoading] = useState(false);
  const [otRules, setOtRules] = useState([]);
  const [otSelection, setOtSelection] = useState([]);
  const [otOptions, setOtOptions] = useState([]);
  const [otFormOpen, setOtFormOpen] = useState(false);
  const [otForm, setOtForm] = useState(defaultOtForm);
  const [workforceStates, setWorkforceStates] = useState([]);
  const [workforceStatesLoading, setWorkforceStatesLoading] = useState(false);
  const [resolvedOtPolicy, setResolvedOtPolicy] = useState(null);
  const [resolvedOtPolicyLoading, setResolvedOtPolicyLoading] = useState(false);
  const [otPolicyInfoOpen, setOtPolicyInfoOpen] = useState(false);
  const [otPolicyInfoLoading, setOtPolicyInfoLoading] = useState(false);
  const [otPolicyInfoRules, setOtPolicyInfoRules] = useState([]);
  const [autoAssignForm, setAutoAssignForm] = useState(defaultAutoAssignForm);
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);
  const [autoAssignSaving, setAutoAssignSaving] = useState(false);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('token') || '';
  const configPropertyId = configDrawerRow?.property_id || configDrawerRow?.id || '';

  const authHeaders = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const propertyGroupNameById = useMemo(() => {
    const map = {};
    propertyGroups.forEach((group) => {
      const id = group.propertyGroupId || group.property_group_id;
      if (!id) return;
      map[String(id)] = group.groupName || group.group_name || '';
    });
    return map;
  }, [propertyGroups]);

  const columns = useMemo(
    () => [
      {
        field: 'logo_url',
        headerName: 'Logo',
        width: 90,
        sortable: false,
        filterable: false,
        resizable: true,
        renderCell: (params) =>
          params.value ? (
            <Avatar
              src={params.value}
              alt={params.row.property_name || 'Property logo'}
              variant="rounded"
              sx={{ width: 32, height: 32 }}
            />
          ) : (
            ''
          ),
      },
      { field: 'property_name', headerName: 'Property Name', width: 220, resizable: true },
      { field: 'property_code', headerName: 'Property Code', width: 140, resizable: true },
      { field: 'legal_name', headerName: 'Legal Name', width: 200, resizable: true },
      { field: 'property_type', headerName: 'Property Type', width: 140, resizable: true },
      {
        field: 'property_group',
        headerName: 'Property Group',
        width: 180,
        resizable: true,
        valueGetter: (params) => propertyGroupNameById[String(params.row.property_group_id || '')] || '',
      },
      { field: 'job_code_level_2', headerName: 'Job Code Level 2', width: 140, resizable: true },
      { field: 'pms_property_id', headerName: 'PMS Property ID', width: 150, resizable: true },
      { field: 'customer_property_id', headerName: 'Customer Property ID', width: 170, resizable: true },
      { field: 'merchant_id', headerName: 'Merchant ID', width: 150, resizable: true },
      { field: 'room_count', headerName: 'Room Count', width: 120, resizable: true },
      { field: 'address_line_1', headerName: 'Address Line 1', width: 200, resizable: true },
      { field: 'address_line_2', headerName: 'Address Line 2', width: 180, resizable: true },
      { field: 'city', headerName: 'City', width: 130, resizable: true },
      { field: 'state_province', headerName: 'State', width: 110, resizable: true },
      { field: 'postal_code', headerName: 'Postal Code', width: 120, resizable: true },
      { field: 'country', headerName: 'Country', width: 110, resizable: true },
      { field: 'phone', headerName: 'Phone', width: 140, resizable: true },
      { field: 'email', headerName: 'Email', width: 180, resizable: true },
      { field: 'currency', headerName: 'Base Currency', width: 120, resizable: true },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        resizable: true,
        renderCell: (p) => ((p.value || '') === '' ? '' : String(p.value).toUpperCase()),
      },
      { field: 'timezone', headerName: 'Timezone', width: 150, resizable: true },
      { field: 'latitude', headerName: 'Latitude', width: 120, resizable: true },
      { field: 'longitude', headerName: 'Longitude', width: 120, resizable: true },
      { field: 'cash_recon_enabled', headerName: 'Cash Recon Enabled', width: 150, resizable: true, renderCell: yesNoChipCell },
      { field: 'allow_overtime', headerName: 'Allow Overtime', width: 140, resizable: true, renderCell: yesNoChipCell },
      { field: 'default_shift_hours', headerName: 'Default Shift Hours', width: 160, resizable: true },
      { field: 'max_hours_per_employee', headerName: 'Max Hours Per Employee', width: 190, resizable: true },
      { field: 'overtime_threshold_hours', headerName: 'Overtime Threshold Hours', width: 200, resizable: true },
      { field: 'labor_cost_budget', headerName: 'Labor Cost Budget', width: 160, resizable: true },
      { field: 'is_corporate_entity', headerName: 'Is Corporate Entity', width: 160, resizable: true, renderCell: yesNoChipCell },
      { field: 'workforce_state_code', headerName: 'OT State', width: 90, resizable: true },
      { field: 'overtime_policy_name', headerName: 'OT Policy', width: 180, resizable: true },
      { field: 'deleted', headerName: 'Deleted', width: 90, resizable: true, renderCell: yesNoChipCell },
    ],
    [propertyGroupNameById]
  );

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [
        row.property_name,
        row.property_code,
        row.legal_name,
        row.property_type,
        propertyGroupNameById[String(row.property_group_id || '')],
        row.job_code_level_2,
        row.pms_property_id,
        row.customer_property_id,
        row.merchant_id,
        row.room_count,
        row.address_line_1,
        row.address_line_2,
        row.city,
        row.state_province,
        row.postal_code,
        row.country,
        row.phone,
        row.email,
        row.currency,
        row.status,
        row.timezone,
        row.latitude,
        row.longitude,
        row.cash_recon_enabled,
        row.allow_overtime,
        row.default_shift_hours,
        row.max_hours_per_employee,
        row.overtime_threshold_hours,
        row.labor_cost_budget,
        row.is_corporate_entity,
        row.workforce_state_code,
        row.overtime_policy_name,
        row.deleted,
      ].some((value) => String(value ?? '').toLowerCase().includes(query))
    );
  }, [propertyGroupNameById, rows, searchQuery]);

  const hasSelected = selectionModel.length > 0;
  const selectedId = selectionModel[0];
  const nextId = useMemo(() => `PROP-${String(rows.length + 1).padStart(2, '0')}`, [rows.length]);

  const mapApiPropertyToRow = (item) => ({
    id: item.propertyId,
    property_uuid: item.propertyId,
    property_id: item.propertyId || '',
    tenant_id: item.tenantId || '',
    pms_property_id: item.pmsPropertyId ?? '',
    customer_property_id: item.customerPropertyId ?? '',
    merchant_id: item.merchantId ?? item.merchant_id ?? item.MID ?? '',
    property_name: item.propertyName || '',
    legal_name: item.legalName ?? '',
    property_code: item.propertyCode || '',
    job_code_level_2: item.jobCodeLevel2 ?? item.job_code_level_2 ?? '',
    property_type: item.propertyType ?? '',
    property_group_id: item.propertyGroupId || '',
    room_count: item.roomCount ?? '',
    address_line_1: item.addressLine1 ?? '',
    address_line_2: item.addressLine2 ?? '',
    city: item.city ?? '',
    state_province: item.state ?? '',
    workforce_state_id: String(item.workforceStateId ?? item.workforce_state_id ?? ''),
    workforce_state_code: item.workforceStateCode ?? item.workforce_state_code ?? '',
    workforce_state_name: item.workforceStateName ?? item.workforce_state_name ?? '',
    overtime_policy_name: item.overtimePolicyName ?? item.overtime_policy_name ?? '',
    postal_code: item.postalCode ?? '',
    country: item.country ?? '',
    latitude: item.latitude ?? '',
    longitude: item.longitude ?? '',
    timezone: item.timezone ?? '',
    phone: item.phone ?? '',
    email: item.email ?? '',
    currency: item.baseCurrency || '',
    cash_recon_enabled: item.cashReconEnabled === true ? 'Yes' : item.cashReconEnabled === false ? 'No' : '',
    default_shift_hours: item.defaultShiftHours ?? '',
    max_hours_per_employee: item.maxHoursPerEmployee ?? '',
    allow_overtime: item.allowOvertime === true ? 'Yes' : item.allowOvertime === false ? 'No' : '',
    overtime_threshold_hours: item.overtimeThresholdHours ?? '',
    labor_cost_budget: item.laborCostBudget ?? '',
    is_corporate_entity: item.isCorporateEntity === true ? 'Yes' : item.isCorporateEntity === false ? 'No' : '',
    status: item.status || '',
    deleted: item.deleted === true ? 'Yes' : 'No',
    created_at: item.createdAt || '',
    updated_at: item.updatedAt || '',
    created_by: item.createdBy ?? '',
    updated_by: item.updatedBy ?? '',
    logo_url: item.logoUrl ?? '',
    logo_s3_key: item.logoS3Key ?? '',
    business_unit_id: item.businessUnitId ?? '',
    company_id: item.companyId ?? '',
    default_bank_account_id: item.defaultBankAccountId ?? '',
  });

  const parseYesNo = (value) => {
    if (value === '' || value == null) return null;
    const normalized = String(value).trim().toUpperCase();
    if (normalized === 'YES' || normalized === 'Y' || normalized === 'TRUE') return true;
    if (normalized === 'NO' || normalized === 'N' || normalized === 'FALSE') return false;
    return null;
  };

  const buildPayload = (currentForm, currentPropertyId) => ({
    tenantId,
    propertyName: currentForm.property_name || null,
    propertyCode: currentForm.property_code || null,
    jobCodeLevel2: currentForm.job_code_level_2 || null,
    propertyGroupId: currentForm.property_group_id?.trim() || null,
    companyId: currentForm.company_id || null,
    businessUnitId: currentForm.business_unit_id || null,
    pmsPropertyId: currentForm.pms_property_id || null,
    customerPropertyId: currentForm.customer_property_id || null,
    merchantId: currentForm.merchant_id || null,
    legalName: currentForm.legal_name || null,
    propertyType: currentForm.property_type || null,
    roomCount: currentForm.room_count === '' ? null : Number(currentForm.room_count),
    addressLine1: currentForm.address_line_1 || null,
    addressLine2: currentForm.address_line_2 || null,
    city: currentForm.city || null,
    state: currentForm.state_province || null,
    workforceStateId: currentForm.workforce_state_id || null,
    overtimePolicyName: currentForm.workforce_state_id
      ? getPolicyNameForState(currentForm.workforce_state_id) || currentForm.overtime_policy_name || null
      : currentForm.overtime_policy_name || null,
    country: currentForm.country || null,
    postalCode: currentForm.postal_code || null,
    latitude: currentForm.latitude === '' ? null : Number(currentForm.latitude),
    longitude: currentForm.longitude === '' ? null : Number(currentForm.longitude),
    timezone: currentForm.timezone || null,
    phone: currentForm.phone || null,
    email: currentForm.email || null,
    baseCurrency: currentForm.currency || null,
    defaultBankAccountId: currentForm.default_bank_account_id || null,
    cashReconEnabled: currentForm.cash_recon_enabled === '' ? true : currentForm.cash_recon_enabled === 'Yes',
    defaultShiftHours: currentForm.default_shift_hours === '' ? null : Number(currentForm.default_shift_hours),
    maxHoursPerEmployee: currentForm.max_hours_per_employee === '' ? null : Number(currentForm.max_hours_per_employee),
    allowOvertime: parseYesNo(currentForm.allow_overtime),
    overtimeThresholdHours: currentForm.overtime_threshold_hours === '' ? null : Number(currentForm.overtime_threshold_hours),
    laborCostBudget: currentForm.labor_cost_budget === '' ? null : Number(currentForm.labor_cost_budget),
    isCorporateEntity: parseYesNo(currentForm.is_corporate_entity),
    logoUrl: currentForm.logo_url || null,
    logoS3Key: currentForm.logo_s3_key || null,
    status: currentForm.status || null,
    createdBy: currentPropertyId ? (currentForm.created_by || username) : username,
    updatedBy: username,
    ...(currentPropertyId ? { propertyId: currentPropertyId } : {}),
  });

  const fetchProperties = async () => {
    if (!tenantId) {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to fetch properties: ${response.status}`);
      }
      const data = await response.json();
      setRows(Array.isArray(data) ? data.map(mapApiPropertyToRow) : []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setRows([]);
      showError(error.message || 'Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromSnowflake = async () => {
    if (!canAdd || !tenantIntegration || importing) return;
    try {
      setImporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/properties/import-from-snowflake?tenant_id=${encodeURIComponent(tenantIntegration)}`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail || data.error || data.message;
        throw new Error(typeof detail === 'string' ? detail : `Import failed: ${response.status}`);
      }
      showSuccess(
        `Imported ${data.inserted ?? 0} properties from Snowflake. ${data.skipped_existing ?? 0} already existed.`
      );
      await fetchProperties();
    } catch (error) {
      console.error('Error importing properties:', error);
      showError(error.message || 'Failed to import properties from Snowflake');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    const loadWorkforceStates = async () => {
      setWorkforceStatesLoading(true);
      try {
        const response = await fetch(`${baseUrl}conductor/workforce-state`, { headers: authHeaders });
        if (!response.ok) throw new Error('Failed to load workforce states');
        const data = await response.json();
        if (!cancelled) setWorkforceStates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setWorkforceStates([]);
      } finally {
        if (!cancelled) setWorkforceStatesLoading(false);
      }
    };
    loadWorkforceStates();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, baseUrl]);

  const getPolicyNameForState = useCallback((stateId) => {
    if (!stateId) return '';
    const selected = workforceStates.find(
      (s) => String(s.state_id || s.stateId) === String(stateId)
    );
    const stateName = selected?.state_name || selected?.stateName || '';
    return (
      selected?.default_policy_name
      || selected?.defaultPolicyName
      || (stateName ? `${stateName} Standard` : '')
    );
  }, [workforceStates]);

  const applyOtPolicyDefaults = (rowData) => {
    if (!rowData.workforce_state_id) {
      return rowData;
    }
    const policyName = getPolicyNameForState(rowData.workforce_state_id);
    if (!policyName) return rowData;
    return { ...rowData, overtime_policy_name: policyName };
  };

  const handleWorkforceStateChange = (stateId) => {
    setForm((prev) => ({
      ...prev,
      workforce_state_id: stateId,
      overtime_policy_name: getPolicyNameForState(stateId),
    }));
    setOtPolicyInfoOpen(false);
    setOtPolicyInfoRules([]);
  };

  const openOtPolicyInfo = async () => {
    if (!form.workforce_state_id) return;
    setOtPolicyInfoOpen(true);
    setOtPolicyInfoLoading(true);
    setOtPolicyInfoRules([]);
    try {
      const res = await fetch(
        `${baseUrl}conductor/workforce-state/${form.workforce_state_id}/overtime-rules`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error('Failed to load state overtime rules');
      const data = await res.json();
      setOtPolicyInfoRules(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(error.message || 'Failed to load policy rules');
      setOtPolicyInfoOpen(false);
    } finally {
      setOtPolicyInfoLoading(false);
    }
  };

  const closeOtPolicyInfo = () => {
    setOtPolicyInfoOpen(false);
    setOtPolicyInfoRules([]);
  };

  const selectedOtStateLabel = useMemo(() => {
    if (!form.workforce_state_id) return '';
    const selected = workforceStates.find(
      (s) => String(s.state_id || s.stateId) === String(form.workforce_state_id)
    );
    if (!selected) return '';
    const code = selected.state_code || selected.stateCode;
    const name = selected.state_name || selected.stateName;
    return `${code} — ${name}`;
  }, [form.workforce_state_id, workforceStates]);

  useEffect(() => {
    if (!openDialog) {
      closeOtPolicyInfo();
      return;
    }
    if (!form.workforce_state_id || workforceStatesLoading || workforceStates.length === 0) {
      return;
    }
    const policyName = getPolicyNameForState(form.workforce_state_id);
    if (policyName && form.overtime_policy_name !== policyName) {
      setForm((prev) => ({ ...prev, overtime_policy_name: policyName }));
    }
  }, [
    openDialog,
    form.workforce_state_id,
    form.overtime_policy_name,
    workforceStates,
    workforceStatesLoading,
    getPolicyNameForState,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadGroups = async () => {
      if (!tenantId) {
        setPropertyGroups([]);
        return;
      }
      setPropertyGroupsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/property-groups/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
          {
            method: 'GET',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch property groups: ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          list.sort((a, b) => (a.groupName || '').localeCompare(b.groupName || '', undefined, { sensitivity: 'base' }));
          setPropertyGroups(list);
        }
      } catch (err) {
        console.error('Error fetching property groups:', err);
        if (!cancelled) setPropertyGroups([]);
      } finally {
        if (!cancelled) setPropertyGroupsLoading(false);
      }
    };
    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleOpenAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setForm({ ...defaultForm, created_by: username, updated_by: username });
    setLogoFile(null);
    setLogoPreviewUrl('');
    setEditingPropertyId(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = async () => {
    if (!canEdit || !hasSelected) return;
    const row = rows.find((r) => r.id === selectedId);
    if (!row) return;
    setMode('edit');
    setEditingPropertyId(row.property_uuid);
    setLogoFile(null);
    setLogoPreviewUrl(row.logo_url || '');
    try {
      const response = await fetch(
        `${baseUrl}conductor/cash-recon/properties/${row.property_uuid}`,
        { headers: authHeaders }
      );
      if (response.ok) {
        const item = await response.json();
        const freshRow = applyOtPolicyDefaults(mapApiPropertyToRow(item));
        setForm({ ...defaultForm, ...freshRow, updated_by: username });
        setLogoPreviewUrl(freshRow.logo_url || '');
      } else {
        setForm({ ...defaultForm, ...applyOtPolicyDefaults(row), updated_by: username });
      }
    } catch {
      setForm({ ...defaultForm, ...applyOtPolicyDefaults(row), updated_by: username });
    }
    setOpenDialog(true);
  };

  const handlePropertyRowClick = (params) => {
    setSelectionModel([params.row.id]);
    setConfigDrawerRow(params.row);
    setConfigDrawerOpen(true);
  };

  const loadOtLookups = useCallback(async () => {
    const tenantQuery = tenantId ? `&tenant_id=${encodeURIComponent(tenantId)}` : '';
    try {
      const otRes = await fetch(`${baseUrl}conductor/lookup-list?list_code=OVERTIME_RULES${tenantQuery}`, {
        headers: authHeaders,
      });
      if (otRes.ok) {
        const data = await otRes.json();
        const items = Array.isArray(data?.list_items) ? data.list_items : [];
        setOtOptions(items.length > 0 ? items : DEFAULT_OT_RULE_OPTIONS);
        return;
      }
    } catch (error) {
      console.error(error);
    }
    setOtOptions(DEFAULT_OT_RULE_OPTIONS);
  }, [authHeaders, baseUrl, tenantId]);

  const loadOtRules = useCallback(async () => {
    if (!tenantId || !configPropertyId) {
      setOtRules([]);
      return;
    }
    setOtLoading(true);
    try {
      const query = new URLSearchParams({ tenant_id: tenantId, property_id: configPropertyId });
      const res = await fetch(`${baseUrl}conductor/workforce-property-overtime-rule?${query}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to load overtime rules');
      const data = await res.json();
      setOtRules((Array.isArray(data) ? data : []).map(mapOtRow));
    } catch (error) {
      showError(error.message || 'Failed to load overtime rules');
      setOtRules([]);
    } finally {
      setOtLoading(false);
    }
  }, [authHeaders, baseUrl, configPropertyId, showError, tenantId]);

  const loadResolvedOtPolicy = useCallback(async () => {
    if (!tenantId || !configPropertyId) {
      setResolvedOtPolicy(null);
      setResolvedOtPolicyLoading(false);
      return;
    }
    setResolvedOtPolicyLoading(true);
    try {
      const query = new URLSearchParams({ tenant_id: tenantId, property_id: configPropertyId });
      const res = await fetch(`${baseUrl}conductor/workforce-overtime-policy/resolved?${query}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to load resolved overtime policy');
      setResolvedOtPolicy(await res.json());
    } catch (error) {
      console.error(error);
      setResolvedOtPolicy(null);
    } finally {
      setResolvedOtPolicyLoading(false);
    }
  }, [authHeaders, baseUrl, configPropertyId, tenantId]);

  const loadAutoAssignPolicy = useCallback(async () => {
    if (!tenantId || !configPropertyId) {
      setAutoAssignForm(defaultAutoAssignForm);
      return;
    }
    setAutoAssignLoading(true);
    try {
      const query = new URLSearchParams({ tenant_id: tenantId, property_id: configPropertyId });
      const res = await fetch(`${baseUrl}conductor/workforce-auto-assign-policy?${query}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to load auto assign policy');
      const data = await res.json();
      setAutoAssignForm(mapAutoAssignPolicy(data));
    } catch (error) {
      showError(error.message || 'Failed to load auto assign policy');
      setAutoAssignForm(defaultAutoAssignForm);
    } finally {
      setAutoAssignLoading(false);
    }
  }, [authHeaders, baseUrl, configPropertyId, showError, tenantId]);

  const saveAutoAssignPolicy = async () => {
    if (!tenantId || !configPropertyId) {
      showError('Select a property first.');
      return;
    }
    setAutoAssignSaving(true);
    try {
      const toNullableInt = (value) => {
        if (value === '' || value == null) return null;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };
      const toNullableDecimal = (value) => {
        if (value === '' || value == null) return null;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };
      const body = {
        auto_assign_policy_id: autoAssignForm.auto_assign_policy_id || null,
        tenant_id: tenantId,
        property_id: configPropertyId,
        enabled: Boolean(autoAssignForm.enabled),
        selection_mode: autoAssignForm.selection_mode || 'EMPLOYEE_ID_ASC',
        max_shifts_per_day: toNullableInt(autoAssignForm.max_shifts_per_day),
        min_rest_hours_between_shifts: toNullableDecimal(autoAssignForm.min_rest_hours_between_shifts),
        max_consecutive_work_days: toNullableInt(autoAssignForm.max_consecutive_work_days),
        prefer_preferred_availability: Boolean(autoAssignForm.prefer_preferred_availability),
        status: autoAssignForm.status || 'ACTIVE',
      };
      const res = await fetch(`${baseUrl}conductor/workforce-auto-assign-policy`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save auto assign policy');
      }
      const saved = await res.json();
      setAutoAssignForm(mapAutoAssignPolicy(saved));
      showSuccess('Auto Assign policy saved');
    } catch (error) {
      showError(error.message || 'Failed to save auto assign policy');
    } finally {
      setAutoAssignSaving(false);
    }
  };

  const hasStateOtPolicy = useMemo(() => {
    if (configDrawerRow?.workforce_state_id) return true;
    return Boolean(
      resolvedOtPolicy?.workforceStateId
      || resolvedOtPolicy?.workforce_state_id
      || resolvedOtPolicy?.stateCode
      || resolvedOtPolicy?.state_code
    );
  }, [configDrawerRow, resolvedOtPolicy]);

  useEffect(() => {
    if (!configDrawerOpen || !configPropertyId) return;
    setOtSelection([]);
    loadOtLookups();
    loadResolvedOtPolicy();
    loadAutoAssignPolicy();
    if (!configDrawerRow?.workforce_state_id) {
      loadOtRules();
    }
  }, [
    configDrawerOpen,
    configPropertyId,
    configDrawerRow,
    loadOtLookups,
    loadOtRules,
    loadResolvedOtPolicy,
    loadAutoAssignPolicy,
  ]);

  const selectedOtRow = useMemo(
    () => otRules.find((row) => row.id === otSelection[0]) || null,
    [otRules, otSelection]
  );

  const otColumns = useMemo(
    () => [
      {
        field: 'rule_name',
        headerName: 'Rule',
        flex: 1,
        minWidth: 160,
        resizable: true,
        valueFormatter: (params) => {
          const opt = otOptions.find((item) => item.code === params.row?.condition_type);
          return opt?.label || params.value;
        },
      },
      { field: 'threshold_hours', headerName: 'Threshold Hrs', width: 120, resizable: true },
      { field: 'multiplier', headerName: 'Multiplier', width: 110, resizable: true },
      { field: 'status', headerName: 'Status', width: 100, resizable: true },
    ],
    [otOptions]
  );

  const openAddOt = async () => {
    if (!canAdd) return;
    setOtForm(defaultOtForm);
    if (otOptions.length === 0) {
      await loadOtLookups();
    }
    setOtFormOpen(true);
  };

  const openEditOt = () => {
    if (!canEdit || !selectedOtRow) return;
    setOtForm({
      overtime_rule_id: selectedOtRow.overtime_rule_id,
      rule_name: selectedOtRow.rule_name,
      condition_type: selectedOtRow.condition_type,
      threshold_hours: selectedOtRow.threshold_hours,
      multiplier: selectedOtRow.multiplier,
      status: selectedOtRow.status,
    });
    setOtFormOpen(true);
  };

  const saveOtRule = async () => {
    if ((otForm.overtime_rule_id && !canEdit) || (!otForm.overtime_rule_id && !canAdd)) return;
    if (!otForm.condition_type) {
      showError('Please select a rule type.');
      return;
    }
    const isEdit = Boolean(otForm.overtime_rule_id);
    const url = isEdit
      ? `${baseUrl}conductor/workforce-property-overtime-rule/${otForm.overtime_rule_id}`
      : `${baseUrl}conductor/workforce-property-overtime-rule`;
    const body = {
      tenant_id: tenantId,
      property_id: configPropertyId,
      rule_name: otForm.rule_name,
      condition_type: otForm.condition_type,
      threshold_hours: Number(otForm.threshold_hours) || 0,
      multiplier: Number(otForm.multiplier) || 1.5,
      status: otForm.status,
    };
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: authHeaders,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to save overtime rule');
    }
    setOtFormOpen(false);
    showSuccess(isEdit ? 'Overtime rule updated.' : 'Overtime rule added.');
    loadOtRules();
    loadResolvedOtPolicy();
  };

  const deleteOt = async (id) => {
    if (!canDelete) return;
    const query = new URLSearchParams({ tenant_id: tenantId, property_id: configPropertyId });
    const res = await fetch(`${baseUrl}conductor/workforce-property-overtime-rule/${id}?${query}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (!res.ok && res.status !== 204) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to delete overtime rule');
    }
    showSuccess('Overtime rule deleted.');
    loadOtRules();
    loadResolvedOtPolicy();
  };

  const handleLogoFileChange = (event) => {
    if (!canUpload) return;
    const file = event.target.files?.[0] || null;
    setLogoFile(file);
    if (!file) {
      setLogoPreviewUrl(form.logo_url || '');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file for logo.');
      event.target.value = '';
      setLogoFile(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setLogoPreviewUrl(String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const handleOpenDeleteDialog = () => {
    if (!canDelete || !hasSelected) return;
    const row = rows.find((r) => r.id === selectedId);
    if (!row?.property_uuid) return;
    setRowPendingDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setRowPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) return;
    const propertyId = rowPendingDelete?.property_uuid;
    if (!propertyId) return;

    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/hard/${propertyId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete property: ${response.status}`);
      }

      setRows((prev) => prev.filter((r) => r.id !== rowPendingDelete.id));
      setSelectionModel([]);
      setDeleteDialogOpen(false);
      setRowPendingDelete(null);
      showSuccess('Property deleted successfully');
    } catch (error) {
      console.error('Error deleting property:', error);
      showError(error.message || 'Failed to delete property');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const payload = buildPayload(form, mode === 'edit' ? editingPropertyId : null);
      const url = mode === 'edit'
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/${editingPropertyId}`
        : `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties`;
      const response = await fetch(url, {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save property: ${response.status}`);
      }

      const saved = await response.json();
      let savedProperty = saved;

      // Upload logo after property create/update succeeds.
      if (logoFile && saved?.propertyId) {
        const logoFormData = new FormData();
        logoFormData.append('file', logoFile);
        const uploadResp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/${saved.propertyId}/logo`,
          {
            method: 'POST',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: logoFormData,
          }
        );
        if (!uploadResp.ok) {
          const uploadErr = await uploadResp.json().catch(() => ({}));
          throw new Error(uploadErr.error || uploadErr.message || `Failed to upload logo: ${uploadResp.status}`);
        }
        savedProperty = await uploadResp.json();
      }

      const mapped = mapApiPropertyToRow(savedProperty);
      if (mode === 'edit') {
        setRows((prev) => prev.map((r) => (r.id === mapped.id ? mapped : r)));
      } else {
        setRows((prev) => [mapped, ...prev]);
      }
      setLogoFile(null);
      setLogoPreviewUrl('');
      setOpenDialog(false);
      showSuccess(mode === 'edit' ? 'Property updated successfully' : 'Property saved successfully');
    } catch (error) {
      console.error('Error saving property:', error);
      showError(error.message || 'Failed to save property');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Property
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            sx={{ ...harmonatePageSearchFieldSx, width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={harmonatePageSearchIconSx} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    aria-label="Clear property search"
                    sx={{ color: ht.white }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            onClick={handleImportFromSnowflake}
            disabled={!canAdd || importing || !tenantIntegration}
            sx={harmonateProceedButtonSx}
          >
            {importing ? 'Refreshing…' : 'Refresh Property'}
          </Button>
          <Tooltip title={canEdit ? 'Edit Property' : 'No edit permission'}>
            <span>
              <IconButton
                onClick={handleOpenEdit}
                disabled={!hasSelected || !canEdit}
                sx={harmonatePlainIconButtonSx}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add Property' : 'No add permission'}>
            <span>
              <IconButton
                onClick={handleOpenAdd}
                disabled={!canAdd}
                sx={harmonatePlainIconButtonSx}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete Property' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={handleOpenDeleteDialog}
                disabled={!hasSelected || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canUpload ? 'Bulk upload properties' : 'No upload permission'}>
            <span>
              <IconButton
                onClick={() => {
                  if (!canUpload) return;
                  setPropertyImportOpen(true);
                }}
                disabled={!canUpload || !tenantId}
                sx={harmonatePlainIconButtonSx}
                aria-label="Bulk upload properties"
              >
                <UploadFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={40}
          autoHeight={false}
          columnResizeMode="onEnd"
          resizable
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(m) => setSelectionModel(m)}
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          loading={loading}
          onRowClick={handlePropertyRowClick}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  {searchQuery.trim() ? 'No properties match your search' : 'No properties available'}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchProperties}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            ),
          }}
        />
      </Paper>

      <PropertyImportDialog
        open={propertyImportOpen}
        onClose={() => setPropertyImportOpen(false)}
        tenantId={tenantId}
        username={username}
        propertyGroups={propertyGroups}
        showSuccess={showSuccess}
        showError={showError}
        onImportSuccess={fetchProperties}
      />

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setLogoFile(null);
          setLogoPreviewUrl('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Property' : 'Edit Property'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* <TextField label="Property ID" value={mode === 'add' ? nextId : form.property_id || ''} size="small" fullWidth disabled /> */}
              {/* <TextField label="Tenant ID" value={tenantId || 'TENANT-01'} size="small" fullWidth disabled /> */}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Avatar
                src={logoPreviewUrl || form.logo_url || ''}
                alt="Property logo preview"
                variant="rounded"
                sx={{ width: 52, height: 52, borderRadius: ht.fieldRadius }}
              />
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={harmonateProceedButtonSx}
                disabled={!canUpload}
              >
                {logoFile ? 'Replace Logo' : 'Upload Logo'}
                {canUpload && (
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleLogoFileChange}
                  />
                )}
              </Button>
              {(logoFile || logoPreviewUrl || form.logo_url) && (
                <Typography
                  variant="caption"
                  sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: ht.text }}
                  title={logoFile ? logoFile.name : 'Existing logo will be kept unless a new file is selected.'}
                >
                  {logoFile ? `Selected: ${logoFile.name}` : 'Current logo attached'}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Property Name" name="property_name" value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} size="small" fullWidth required />
              <TextField label="Legal Name" name="legal_name" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ width: '100%' }}>
              <TextField
                label="Property Group"
                name="property_group_id"
                value={form.property_group_id}
                onChange={(e) => setForm({ ...form, property_group_id: e.target.value })}
                size="small"
                fullWidth
                select
                InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: harmonateSelectMenuProps }}
                disabled={propertyGroupsLoading}
                helperText="Optional. Leave blank to save a property without a group."
              >
                <MenuItem value="">
                  <em>No property group</em>
                </MenuItem>
                {propertyGroups.map((g) => (
                  <MenuItem key={g.propertyGroupId} value={g.propertyGroupId}>
                    {g.groupName || g.propertyGroupId}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Property Code" name="property_code" value={form.property_code} onChange={(e) => setForm({ ...form, property_code: e.target.value })} size="small" fullWidth required />
              <TextField
                label="Job Code Level 2"
                name="job_code_level_2"
                value={form.job_code_level_2}
                onChange={(e) => setForm({ ...form, job_code_level_2: e.target.value })}
                size="small"
                fullWidth
                helperText="Optional location / business unit job code for employees"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Property Type" name="property_type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} size="small" fullWidth select SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
                <MenuItem value="HOTEL">HOTEL</MenuItem><MenuItem value="RESORT">RESORT</MenuItem><MenuItem value="EXTENDED_STAY">EXTENDED_STAY</MenuItem><MenuItem value="OTHER">OTHER</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="PMS Property ID" name="pms_property_id" value={form.pms_property_id} onChange={(e) => setForm({ ...form, pms_property_id: e.target.value })} size="small" fullWidth />
              <TextField label="Customer Property ID" name="customer_property_id" value={form.customer_property_id} onChange={(e) => setForm({ ...form, customer_property_id: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Merchant ID"
                name="merchant_id"
                value={form.merchant_id}
                onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                size="small"
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Room Count" name="room_count" type="number" value={form.room_count} onChange={(e) => setForm({ ...form, room_count: e.target.value })} size="small" fullWidth />
              <TextField label="Timezone" name="timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Address Line 1" name="address_line_1" value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} size="small" fullWidth />
              <TextField label="Address Line 2" name="address_line_2" value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="City" name="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} size="small" fullWidth />
              <TextField label="State/Province" name="state_province" value={form.state_province} onChange={(e) => setForm({ ...form, state_province: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Zip Code" name="postal_code" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} size="small" fullWidth />
              <TextField label="Country" name="country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label="Workforce OT State"
                name="workforce_state_id"
                value={form.workforce_state_id}
                onChange={(e) => handleWorkforceStateChange(e.target.value)}
                size="small"
                fullWidth
                select
                disabled={workforceStatesLoading}
                sx={{ flex: 1 }}
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              >
                <MenuItem value="">
                  <em>Select state</em>
                </MenuItem>
                {workforceStates.map((state) => {
                  const id = String(state.state_id || state.stateId || '');
                  const code = state.state_code || state.stateCode;
                  const name = state.state_name || state.stateName;
                  return (
                    <MenuItem key={id} value={id}>
                      {code} — {name}
                    </MenuItem>
                  );
                })}
              </TextField>
              <Box sx={{ flex: 1, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                <TextField
                  label="Overtime Policy Name"
                  name="overtime_policy_name"
                  value={form.workforce_state_id ? getPolicyNameForState(form.workforce_state_id) : form.overtime_policy_name}
                  onChange={(e) => setForm({ ...form, overtime_policy_name: e.target.value })}
                  size="small"
                  fullWidth
                  disabled={Boolean(form.workforce_state_id)}
                  placeholder="e.g. California Hotel Policy"
                />
                <Tooltip title="View state overtime policy rules">
                  <span>
                    <IconButton
                      size="small"
                      onClick={openOtPolicyInfo}
                      disabled={!form.workforce_state_id}
                      aria-label="View overtime policy rules"
                      sx={{ mt: 0.5, color: ht.text }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Latitude" name="latitude" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} size="small" fullWidth />
              <TextField label="Longitude" name="longitude" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Phone" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} size="small" fullWidth />
              <TextField label="Email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} size="small" fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Currency" name="currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} size="small" fullWidth />
              <TextField label="Status" name="status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} size="small" fullWidth select SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem><MenuItem value="INACTIVE">INACTIVE</MenuItem><MenuItem value="PRE_OPENING">PRE_OPENING</MenuItem><MenuItem value="CLOSED">CLOSED</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDialog(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={otPolicyInfoOpen} onClose={closeOtPolicyInfo} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: ht.white, fontSize: '1.25rem' }}>
              {getPolicyNameForState(form.workforce_state_id) || 'Overtime Policy'}
            </Typography>
            {selectedOtStateLabel ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: ht.secondaryTab }}>
                {selectedOtStateLabel}
              </Typography>
            ) : null}
          </Box>
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: ht.text }}>
            State Overtime Rules
          </Typography>
          {otPolicyInfoLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={22} sx={{ color: ht.text }} />
              <Typography variant="body2" sx={{ color: ht.text }}>Loading rules…</Typography>
            </Box>
          ) : otPolicyInfoRules.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {otPolicyInfoRules.map((rule) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={`${rule.condition_type || rule.conditionType}-${rule.rule_name || rule.ruleName}`}
                  sx={{ mb: 1, color: ht.text }}
                >
                  {rule.rule_name || rule.ruleName}: {rule.condition_type || rule.conditionType} @ {rule.threshold_hours ?? rule.thresholdHours}h × {rule.multiplier}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: ht.text }}>
              No overtime rules defined for this state.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeOtPolicyInfo} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete the property &quot;
            {rowPendingDelete?.property_name || rowPendingDelete?.property_code || 'selected'}
            &quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={configDrawerOpen}
        onClose={() => setConfigDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 720 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: ht.drawerBg,
          },
        }}
      >
        <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
              {configDrawerRow?.property_name || configDrawerRow?.property_code || 'Property'}
            </Typography>
            <Typography variant="body2" sx={{ color: ht.secondaryTab }}>
              Property
            </Typography>
          </Box>
          <IconButton onClick={() => setConfigDrawerOpen(false)} aria-label="Close drawer" sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }}>
          <Paper sx={{ p: 1.5, mb: 1, bgcolor: ht.listBg, border: `1px solid ${ht.line}`, borderRadius: ht.cardRadius, boxShadow: 'none' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: ht.text }}>
              State Overtime Policy
            </Typography>
            {resolvedOtPolicyLoading ? (
              <Typography variant="body2" sx={{ color: ht.text }}>Loading policy…</Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: ht.text }}>
                  {resolvedOtPolicy?.state_name || resolvedOtPolicy?.stateName || 'No state selected'}
                  {resolvedOtPolicy?.overtime_policy_name || resolvedOtPolicy?.overtimePolicyName
                    ? ` — ${resolvedOtPolicy.overtime_policy_name || resolvedOtPolicy.overtimePolicyName}`
                    : ''}
                </Typography>
                {(resolvedOtPolicy?.resolved_rules || resolvedOtPolicy?.resolvedRules || []).length > 0 ? (
                  <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
                    {(resolvedOtPolicy.resolved_rules || resolvedOtPolicy.resolvedRules).map((rule) => (
                      <Typography component="li" variant="body2" key={`${rule.condition_type || rule.conditionType}-${rule.rule_name || rule.ruleName}`} sx={{ color: ht.text }}>
                        {rule.rule_name || rule.ruleName}: {rule.condition_type || rule.conditionType} @ {rule.threshold_hours ?? rule.thresholdHours}h × {rule.multiplier}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ mt: 0.5, color: ht.text }}>
                    Select a workforce OT state on the property to apply state rules.
                  </Typography>
                )}
              </>
            )}
          </Paper>
          <Box sx={harmonateDrawerAccordionGroupSx}>
          {!hasStateOtPolicy && (
          <Accordion
            expanded={drawerAccordionExpanded === 'overtime'}
            onChange={(_, isExpanded) => setDrawerAccordionExpanded(isExpanded ? 'overtime' : false)}
            disableGutters
            elevation={0}
            sx={harmonateDrawerAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
              <Typography sx={harmonateDrawerAccordionTitleSx}>Property Overtime Rules</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ ...harmonateDrawerAccordionDetailsSx, height: 420, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5, gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Tooltip title="Add Overtime Rule">
                    <IconButton size="small" onClick={openAddOt} disabled={!configPropertyId || !canAdd} sx={harmonateGridActionIconSx}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Overtime Rule">
                    <span>
                      <IconButton size="small" onClick={openEditOt} disabled={!selectedOtRow || !canEdit} sx={harmonateGridActionIconSx}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete Overtime Rule">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!selectedOtRow || !canDelete}
                        sx={harmonateGridActionIconSx}
                        onClick={() => {
                          if (!canDelete || !selectedOtRow?.overtime_rule_id) return;
                          deleteOt(selectedOtRow.overtime_rule_id).catch((e) => showError(e.message));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Paper sx={{ flex: 1, borderRadius: 0, overflow: 'hidden', boxShadow: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <DataGridPro
                    rows={otRules}
                    columns={otColumns}
                    loading={otLoading}
                    rowSelectionModel={otSelection}
                    onRowSelectionModelChange={(model) => setOtSelection(model)}
                    disableMultipleRowSelection
                    pagination
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    sx={{ ...harmonateDataGridSx, border: 'none', flex: 1 }}
                  />
                </Paper>
              </Box>
            </AccordionDetails>
          </Accordion>
          )}

          <Accordion
            expanded={drawerAccordionExpanded === 'auto-assign'}
            onChange={(_, isExpanded) => setDrawerAccordionExpanded(isExpanded ? 'auto-assign' : false)}
            disableGutters
            elevation={0}
            sx={harmonateDrawerAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
              <Typography sx={harmonateDrawerAccordionTitleSx}>Auto Assign Rules</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                ...harmonateDrawerAccordionDetailsSx,
                ...harmonateFormFieldsSx,
                gap: 1.5,
                p: 2,
              }}
            >
              {autoAssignLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={20} sx={{ color: ht.text }} />
                  <Typography variant="body2" sx={{ color: ht.text }}>Loading policy…</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: ht.text }}>
                    Leave numeric fields blank for no limit. Disable to keep legacy Auto Assign (first eligible employee).
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(autoAssignForm.enabled)}
                        onChange={(e) =>
                          setAutoAssignForm((prev) => ({ ...prev, enabled: e.target.checked }))
                        }
                        sx={harmonateSwitchSx}
                      />
                    }
                    label="Enable fair Auto Assign policy"
                  />
                  <TextField
                    select
                    label="Selection mode"
                    size="small"
                    fullWidth
                    value={autoAssignForm.selection_mode}
                    onChange={(e) =>
                      setAutoAssignForm((prev) => ({ ...prev, selection_mode: e.target.value }))
                    }
                    disabled={!autoAssignForm.enabled}
                    SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                  >
                    {AUTO_ASSIGN_SELECTION_MODES.map((mode) => (
                      <MenuItem key={mode.code} value={mode.code}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      label="Max shifts / day"
                      type="number"
                      size="small"
                      fullWidth
                      value={autoAssignForm.max_shifts_per_day}
                      onChange={(e) =>
                        setAutoAssignForm((prev) => ({ ...prev, max_shifts_per_day: e.target.value }))
                      }
                      disabled={!autoAssignForm.enabled}
                      inputProps={{ min: 1, step: 1 }}
                      helperText="Blank = no cap"
                    />
                    <TextField
                      label="Min rest hours"
                      type="number"
                      size="small"
                      fullWidth
                      value={autoAssignForm.min_rest_hours_between_shifts}
                      onChange={(e) =>
                        setAutoAssignForm((prev) => ({
                          ...prev,
                          min_rest_hours_between_shifts: e.target.value,
                        }))
                      }
                      disabled={!autoAssignForm.enabled}
                      inputProps={{ min: 0, step: 0.5 }}
                      helperText="Blank = no rest rule"
                    />
                  </Box>
                  <TextField
                    label="Max consecutive work days"
                    type="number"
                    size="small"
                    fullWidth
                    value={autoAssignForm.max_consecutive_work_days}
                    onChange={(e) =>
                      setAutoAssignForm((prev) => ({
                        ...prev,
                        max_consecutive_work_days: e.target.value,
                      }))
                    }
                    disabled={!autoAssignForm.enabled}
                    inputProps={{ min: 1, step: 1 }}
                    helperText="Blank = no consecutive-day cap"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(autoAssignForm.prefer_preferred_availability)}
                        onChange={(e) =>
                          setAutoAssignForm((prev) => ({
                            ...prev,
                            prefer_preferred_availability: e.target.checked,
                          }))
                        }
                        disabled={!autoAssignForm.enabled}
                        sx={harmonateSwitchSx}
                      />
                    }
                    label="Prefer employees matching preferred availability"
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => saveAutoAssignPolicy()}
                      disabled={autoAssignSaving || !configPropertyId}
                      sx={harmonateProceedButtonSx}
                    >
                      {autoAssignSaving ? 'Saving…' : 'Save Auto Assign Rules'}
                    </Button>
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={otFormOpen} onClose={() => setOtFormOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{otForm.overtime_rule_id ? 'Edit Overtime Rule' : 'Add Overtime Rule'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            select
            label="Rule Type"
            value={otForm.condition_type}
            onChange={(e) => {
              const opt = otOptions.find((item) => item.code === e.target.value);
              setOtForm((prev) => ({
                ...prev,
                condition_type: e.target.value,
                rule_name: opt?.label || prev.rule_name,
                threshold_hours: opt?.defaultThreshold ?? prev.threshold_hours,
                multiplier: opt?.defaultMultiplier ?? prev.multiplier,
              }));
            }}
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: harmonateSelectMenuProps,
              renderValue: (selected) => {
                if (!selected) return 'Select rule type';
                const opt = otOptions.find((item) => item.code === selected);
                return opt?.label || selected;
              },
            }}
            sx={harmonateTextFieldSx}
          >
            <MenuItem value="">
              <em>Select rule type</em>
            </MenuItem>
            {otOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label || opt.code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Threshold Hours"
            type="number"
            value={otForm.threshold_hours}
            onChange={(e) => setOtForm((prev) => ({ ...prev, threshold_hours: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            inputProps={{ min: 0, step: 0.5 }}
            sx={harmonateTextFieldSx}
          />
          <TextField
            label="Multiplier"
            type="number"
            value={otForm.multiplier}
            onChange={(e) => setOtForm((prev) => ({ ...prev, multiplier: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            inputProps={{ min: 0.1, step: 0.1 }}
            sx={harmonateTextFieldSx}
          />
          <TextField
            select
            label="Status"
            value={otForm.status}
            onChange={(e) => setOtForm((prev) => ({ ...prev, status: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
            sx={harmonateTextFieldSx}
          >
            {OT_LINE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOtFormOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={() => saveOtRule().catch((e) => showError(e.message))} sx={harmonateProceedButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default Property;
